import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Briefcase, ChevronDown, MapPin } from "lucide-react";
import { Marquee, MaskedLines, Reveal, SectionTag } from "@/components/ui-bits";
import { CAREER_CATEGORIES, CAREER_FAQS, IMAGES, WHY_TARK } from "@/content/site";
import api from "@/lib/api";

const CareersHero = () => (
  <section className="relative overflow-hidden pt-44 pb-24" data-testid="careers-hero">
    <div className="absolute inset-0">
      <img src={IMAGES.careers} alt="" className="h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-tark-bg/70 via-tark-bg/85 to-tark-bg" />
    </div>
    <div className="absolute inset-0 hero-grid opacity-40 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }}
                className="font-mono text-[11px] uppercase tracking-[0.4em] text-tark-cyan">
        Careers at TARK
      </motion.p>
      <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
        <MaskedLines lines={["Build Technology", "That Creates Impact."]} lineClassName="last:text-gradient-cyan" />
      </h1>
      <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
                className="mt-7 max-w-2xl text-base leading-relaxed text-tark-mut sm:text-lg">
        Join TARK and work on modern software, cloud, DevOps, data and AI engineering challenges.
      </motion.p>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
                  className="mt-9 flex flex-wrap gap-4">
        <a href="#openings" data-testid="careers-explore-opportunities"
           className="group inline-flex items-center gap-2 rounded-full bg-tark-blue px-7 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-tark-vivid">
          Explore Opportunities <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </a>
        <a href="#life-at-tark" data-testid="careers-life-at-tark"
           className="inline-flex items-center rounded-full border border-tark-line px-7 py-3.5 font-display text-sm font-semibold text-tark-ink transition-colors hover:border-tark-cyan/50 hover:text-tark-cyan">
          Life at TARK
        </a>
      </motion.div>
    </div>
  </section>
);

const Openings = () => {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const filtered = useMemo(
    () => (filter === "All" ? jobs : jobs.filter((j) => j.department === filter)),
    [jobs, filter]
  );

  return (
    <section id="openings" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 lg:px-10" data-testid="careers-openings">
      <Reveal>
        <SectionTag>Current Openings</SectionTag>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Find your role</h2>
      </Reveal>

      <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-2">
        {["All", ...CAREER_CATEGORIES].map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            data-testid={`career-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`rounded-full border px-4 py-2 font-mono text-xs transition-colors ${
              filter === cat
                ? "border-tark-cyan/60 bg-tark-cyan/10 text-tark-cyan"
                : "border-tark-line text-tark-mut hover:border-tark-cyan/40 hover:text-tark-ink"
            }`}
          >
            {cat}
          </button>
        ))}
      </Reveal>

      <div className="mt-10 space-y-4">
        {loaded && filtered.length === 0 && (
          <p className="rounded-2xl border border-tark-line bg-tark-card p-10 text-center font-mono text-sm text-tark-dim" data-testid="no-jobs-message">
            No open roles in this category right now. Check back soon — or apply through any open role and tell us where you fit.
          </p>
        )}
        <AnimatePresence mode="popLayout">
          {filtered.map((job) => (
            <motion.div key={job.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <Link
                to={`/careers/jobs/${job.id}`}
                data-testid={`job-card-${job.id}`}
                className="card-glow group flex flex-col gap-4 rounded-2xl border border-tark-line bg-tark-card p-7 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 className="font-display text-lg font-bold tracking-tight group-hover:text-tark-cyan">{job.title}</h3>
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 font-mono text-xs text-tark-dim">
                    <span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />{job.department}</span>
                    {job.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{job.location}</span>}
                    {job.experience && <span>{job.experience}</span>}
                    {job.work_mode && <span className="rounded border border-tark-line px-2 py-0.5">{job.work_mode}</span>}
                    {job.employment_type && <span className="rounded border border-tark-line px-2 py-0.5">{job.employment_type}</span>}
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-tark-blue transition-colors group-hover:text-tark-cyan">
                  View Role <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
};

const WhyTark = () => (
  <section id="why-tark" className="border-y border-tark-line bg-tark-surface/40 py-24" data-testid="why-tark">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <SectionTag>Why TARK</SectionTag>
        <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          Where serious engineers do serious work
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {WHY_TARK.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.08}>
            <div className="card-glow h-full rounded-2xl border border-tark-line bg-tark-card p-8">
              <span className="font-mono text-xs text-tark-cyan">0{i + 1}</span>
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-tark-mut">{item.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const HiringProcess = () => {
  const [stages, setStages] = useState([]);

  useEffect(() => {
    api.get("/hiring-stages").then((r) => setStages(r.data)).catch(() => {});
  }, []);

  return (
    <section id="hiring-process" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 lg:px-10" data-testid="hiring-process">
      <Reveal>
        <SectionTag>Hiring Process</SectionTag>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          How we hire
        </h2>
        <p className="mt-4 max-w-2xl text-sm text-tark-mut">
          A transparent process designed to respect your time. Stages may vary by role.
        </p>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {stages.map((stage, i) => (
          <Reveal key={stage.id || stage.title} delay={(i % 3) * 0.07}>
            <div className="relative h-full overflow-hidden rounded-2xl border border-tark-line bg-tark-card p-7" data-testid={`hiring-stage-${i}`}>
              <span className="absolute -right-3 -top-6 font-display text-7xl font-extrabold text-tark-line/60">{i + 1}</span>
              <h3 className="relative font-display text-lg font-bold tracking-tight">{stage.title}</h3>
              <p className="relative mt-3 text-sm leading-relaxed text-tark-mut">{stage.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const LifeAtTark = () => (
  <section id="life-at-tark" className="border-y border-tark-line bg-tark-surface/40 py-24" data-testid="life-at-tark">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-10">
      <Reveal>
        <div className="clip-frame overflow-hidden rounded-2xl border border-tark-line">
          <img src={IMAGES.engineering} alt="Life at TARK" className="aspect-[4/3] w-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <SectionTag>Life at TARK</SectionTag>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          Real systems. Real stakes. Real growth.
        </h2>
        <p className="mt-6 text-base leading-relaxed text-tark-mut">
          At TARK you work on production platforms for real businesses: cloud migrations,
          AI systems, SaaS products and the pipelines behind them. Small senior teams,
          direct client exposure, and the kind of problems that make you better.
        </p>
        <ul className="mt-8 space-y-3">
          {["Modern stacks across cloud, data and AI", "Direct ownership from design to production",
            "Learning hours, certifications and internal guilds", "Clear growth tracks in engineering and leadership"].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-tark-mut">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-tark-cyan" />{item}
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

const FAQs = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="mx-auto max-w-4xl px-6 py-24 lg:px-10" data-testid="careers-faqs">
      <Reveal className="text-center">
        <SectionTag>FAQs</SectionTag>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">Questions, answered</h2>
      </Reveal>
      <div className="mt-12 space-y-3">
        {CAREER_FAQS.map((faq, i) => (
          <Reveal key={faq.q} delay={i * 0.05}>
            <div className="overflow-hidden rounded-2xl border border-tark-line bg-tark-card">
              <button
                className="flex w-full items-center justify-between gap-4 p-6 text-left font-display text-base font-semibold text-tark-ink"
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-toggle-${i}`}
                aria-expanded={open === i}
              >
                {faq.q}
                <ChevronDown className={`h-4 w-4 shrink-0 text-tark-cyan transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                    <p className="px-6 pb-6 text-sm leading-relaxed text-tark-mut">{faq.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Careers = () => (
  <>
    <CareersHero />
    <Marquee items={CAREER_CATEGORIES} />
    <Openings />
    <WhyTark />
    <HiringProcess />
    <LifeAtTark />
    <FAQs />
  </>
);

export default Careers;
